import { useCallback, useMemo } from "react"
import { useSearchParams } from "react-router-dom"

// Syncs a flat filter object to the URL query string so filters persist across
// navigation and are shareable. Only the keys present in `defaults` are read /
// written; empty values are dropped from the URL to keep it clean.
//
// Returns { filters, setFilters(patch), resetFilters() }.
export const useProcurementFilters = (defaults = {}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  // Routes sharing one component (Orders vs Group Orders) keep the instance
  // and the same empty searchParams, so defaults must invalidate the memo too.
  const defaultsKey = JSON.stringify(defaults)

  const filters = useMemo(() => {
    const out = JSON.parse(defaultsKey)
    Object.keys(out).forEach((key) => {
      const v = searchParams.get(key)
      if (v !== null) out[key] = v
    })
    return out
  }, [searchParams, defaultsKey])

  const setFilters = useCallback(
    (patch = {}) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") {
              next.delete(key)
            } else {
              next.set(key, String(value))
            }
          })
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const resetFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        Object.keys(defaults).forEach((key) => next.delete(key))
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  return { filters, setFilters, resetFilters }
}

export default useProcurementFilters
